// import { duration } from "moment"

import { useMemo } from "react"

import { Dayjs } from "dayjs"
import { useSelector } from "react-redux"

import { SvgIcon } from "@components/common"
import YourSuggestions from "@components/ui/YourSuggestions"
import { selectActiveTeeupGameplans } from "@selectors/activeTeeup"
import { selectCalendarEvents } from "@selectors/calendar"
import { selectGamePlanWhenFromCreate } from "@selectors/createTeeUp"
import { selectUserTimezone } from "@selectors/user"
import { checkIfEventsConflict } from "@utils/calendarConflictUtils"
import { getHoursData } from "@utils/suggestionUtils"
import Pill from "components/ui/Pill"
import { useSnackbarStore } from "stores/snackbarStore"
import { useSuggestionDurationStore } from "stores/suggestions/suggestionDurationStore"
import { useSuggestionTimeWheelStore } from "stores/suggestions/suggestionTimeWheelStore"
import { Suggestion, TimeWheelSuggestion } from "types/suggestions"
import dayjs from "utils/dayjs"

const YourSuggestionsContainer = ({ onSave, isFromCreateTeeUp }) => {
  const currentDate = useSuggestionTimeWheelStore(state => state.currentDate)
  const setCurrentDate = useSuggestionTimeWheelStore(
    state => state.setCurrentDate
  )
  const suggestions = useSuggestionTimeWheelStore(state => state.suggestions)
  const addSuggestion = useSuggestionTimeWheelStore(
    state => state.addSuggestion
  )
  const removeSuggestion = useSuggestionTimeWheelStore(
    state => state.removeSuggestion
  )
  const updateSuggestion = useSuggestionTimeWheelStore(
    state => state.updateSuggestion
  )
  const setCurrentActiveSuggestionIndex = useSuggestionTimeWheelStore(
    state => state.setCurrentActiveSuggestionIndex
  )
  const currentActiveSuggestionIndex = useSuggestionTimeWheelStore(
    state => state.currentActiveSuggestionIndex
  )
  const updateCurrentSuggestionTrigger = useSuggestionTimeWheelStore(
    state => state.updateCurrentSuggestionTrigger
  )
  const updateOnAddAnotherClickTrigger = useSuggestionTimeWheelStore(
    state => state.updateOnAddAnotherClickTrigger
  )

  const duration = useSuggestionDurationStore(state => state.duration)
  const setDuration = useSuggestionDurationStore(state => state.setDuration)
  const setSnackbarConfig = useSnackbarStore(state => state.setSnackbarConfig)
  const gameplanWhen = useSelector(selectActiveTeeupGameplans).find(
    gameplan => gameplan.type === "when"
  )
  const createGameplanWhen = useSelector(selectGamePlanWhenFromCreate)
  const timezone = useSelector(selectUserTimezone)

  const isShown = !!currentDate || suggestions.length > 0

  const calendarEvents = useSelector(selectCalendarEvents)

  // C2 fix (2026-07-03): the rebuilt SuggestWhenModalNd (TimeWheelTab) had ZERO conflict wiring
  // -- the conflict-capable wheel only existed in the deprecated SuggestWhenModal/zonesContainer
  // path. Non-blocking: this only surfaces a warning, it never prevents suggest/decide. Checks
  // the CURRENTLY COMPOSED time (currentDate + duration, the same value the defaultPill below
  // renders) against (i) the user's synced calendar (selectCalendarEvents -- empty on dev until
  // the calendar-events endpoint ships, see C1/C2 data caveat) and (ii) this CO's existing When
  // suggestions (decided or pending), reusing the shared checkIfEventsConflict predicate.
  const composeConflictWarning = useMemo(() => {
    if (!currentDate) return null

    const composedEvent = {
      startDate: currentDate,
      endDate: currentDate.add(+duration.id, "minute"),
    }

    const calendarEventsValues = Object.values(calendarEvents || {})
    const conflictingCalendarEvent = calendarEventsValues.find((event: any) =>
      checkIfEventsConflict(composedEvent, event)
    ) as any

    if (conflictingCalendarEvent) {
      const eventTitle =
        conflictingCalendarEvent?.summary ||
        conflictingCalendarEvent?.title ||
        "a calendar event"
      return `Conflicts with your calendar: ${eventTitle}`
    }

    const currentGameplanSuggestions: Suggestion[] =
      (isFromCreateTeeUp
        ? createGameplanWhen?.suggestions
        : gameplanWhen?.suggestions) || []

    const conflictsWithExistingSuggestion = currentGameplanSuggestions.some(
      item => checkIfEventsConflict(composedEvent, item)
    )

    if (conflictsWithExistingSuggestion) {
      return "Conflicts with an existing suggestion"
    }

    return null
  }, [
    currentDate,
    duration,
    calendarEvents,
    gameplanWhen,
    createGameplanWhen,
    isFromCreateTeeUp,
  ])

  const checkAlreadyExistedSuggestions = () => {
    const alreadyExist = suggestions.some(
      (item, index) =>
        item.date[0]?.isSame(currentDate, "hour") &&
        item.date[0]?.isSame(currentDate, "minute") &&
        index !== currentActiveSuggestionIndex
    )
    const currentGameplanSuggestions =
      (isFromCreateTeeUp
        ? createGameplanWhen?.suggestions
        : gameplanWhen?.suggestions) || []

    const alreadyExistInGameplan = currentGameplanSuggestions.some(
      (item: Suggestion) =>
        dayjs(item.startDate)?.tz(timezone)?.isSame(currentDate, "minute")
    )

    if (alreadyExist || alreadyExistInGameplan) {
      setSnackbarConfig({
        message: "Some of suggestions are on same time",
        open: true,
      })
      return true
    }

    return false
  }

  const onAddAnotherClick = () => {
    if (checkAlreadyExistedSuggestions()) return

    const durationNum = +duration.id
    if (!currentDate) return

    // setCurrentDate(dayjs())
    addSuggestion({
      date: [currentDate, currentDate.add(durationNum, "minutes")],
      duration: durationNum,
    })
    const isEmpty = suggestions.length === 0
    if (isEmpty) {
      addSuggestion({
        date: [currentDate, currentDate.add(durationNum, "minutes")],
        duration: durationNum,
      })
    }
    updateOnAddAnotherClickTrigger()
    setDuration({
      id: "60",
      value: "1 hour",
    })
    setCurrentActiveSuggestionIndex(
      isEmpty ? suggestions.length + 1 : suggestions.length
    )
  }

  const handleSuggestionClick = (date: TimeWheelSuggestion, index: number) => {
    const duration = getHoursData().find(d => d.id === date.duration.toString())
    setCurrentDate(date[0])
    if (duration) {
      setDuration(duration)
    }
    setCurrentActiveSuggestionIndex(index)
    updateCurrentSuggestionTrigger()
  }

  const handleSuggestClick = (isDecided: boolean) => {
    // Deciding a time that matches an existing suggestion is a valid
    // action (not a duplicate) -- only guard plain new suggestions.
    if (!isDecided && checkAlreadyExistedSuggestions()) return

    if (suggestions.length > 0) {
      onSave(
        suggestions.map(s => ({
          date: [s.date[0]?.format(), s.date[1]?.format()],
          duration: +duration.id,
        })),
        isDecided
      )
    } else if (currentDate) {
      onSave(
        [
          {
            date: [
              currentDate.format(),
              currentDate.add(+duration.id, "minute").format(),
            ],
            duration: +duration.id,
          },
        ],
        isDecided
      )
    }
  }

  return (
    <>
      {isShown && composeConflictWarning && (
        <div
          data-testid="suggest-when-conflict-warning"
          style={{
            color: "var(--red2)",
            fontSize: "13px",
            fontWeight: 500,
            padding: "4px 0",
          }}>
          {composeConflictWarning}
        </div>
      )}

      <YourSuggestions
        isShown={isShown}
      suggestions={suggestions}
      onAddAnotherClick={onAddAnotherClick}
      onDecideClick={() => handleSuggestClick(true)}
      onSuggestClick={() => handleSuggestClick(false)}
      defaultPill={
        <Pill isSelected>
          {duration.id === "1440" ? (
            <>
              <span>{currentDate?.tz(timezone)?.format("ddd, MMM D")}</span>
              <span className="-ml-1 font-normal">· All day</span>
            </>
          ) : (
            <>
              <span>{currentDate?.tz(timezone)?.format("ddd, MMM D")} </span>
              <span>
                · {currentDate?.tz(timezone).format("h:mma")} -{" "}
                {currentDate
                  ?.add(+duration.id, "minute")
                  ?.tz(timezone)
                  .format("h:mma")}
              </span>
            </>
          )}
        </Pill>
      }
      // @ts-ignore
      renderPill={(pill: TimeWheelSuggestion, index) => (
        <Pill
          isSelected={index === currentActiveSuggestionIndex}
          onClick={() => handleSuggestionClick(pill, index)}>
          {pill.duration === 1440 ? (
            <>
              <span>{pill.date[0]?.tz(timezone)?.format("ddd, MMM D")}</span>
              <span className="-ml-1 font-normal">· All day</span>
            </>
          ) : (
            <>
              <span>{pill.date[0]?.tz(timezone)?.format("ddd, MMM D")}</span>
              <span className="-ml-1">
                · {pill.date[0]?.tz(timezone)?.format("h:mma")} -{" "}
                {pill.date[0]
                  ?.add(+pill.duration, "minute")
                  ?.tz(timezone)
                  .format("h:mma")}
              </span>
            </>
          )}

          {suggestions.length > 1 && (
            <SvgIcon name="cross" onClick={() => removeSuggestion(index)} />
          )}
        </Pill>
      )}
      />
    </>
  )
}

export default YourSuggestionsContainer
